<template>
	<div>
		<AppMenu ref="menuRef" @open="isMenuOpened = true" @close="onClose" menuClasses="tw-py-0">
			<template #trigger>
				<div
					class="tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-font-semibold"
				>
					<InlineSvg
						class="tw-text-gray-600"
						:src="loadFile('assets/v2/image/icons/calendar.svg')"
					/>
					<div>{{ periodLabel(period.label) }}</div>
					<InlineSvg
						class="tw-text-gray-600 tw-transition"
						:class="{ 'tw-rotate-180': isMenuOpened }"
						:src="loadFile('assets/v2/image/icons/arrow-down.svg')"
					/>
				</div>
			</template>
			<div
				v-if="!customSelected"
				class="tw-flex tw-flex-col tw-gap-2 tw-p-3 tw-text-center tw-text-gray-900"
				style="width: 160px"
			>
				<div
					v-for="(option, i) in options"
					:key="i"
					@click.stop="selectOption(option)"
					class="tw-cursor-pointer tw-rounded-lg tw-p-2 hover:tw-bg-gray-100"
					:class="period.id === option.id ? 'tw-bg-gray-100' : ''"
				>
					{{ option.label }}
				</div>
			</div>
			<VueDatePicker
				v-model="date"
				:range="{ partialRange: false }"
				:inline="{ input: false }"
				:enable-time-picker="false"
				v-else
			>
				<template #action-row="{ selectDate, internalModelValue }">
					<div class="tw-flex tw-w-full tw-gap-2 tw-border-t tw-border-gray-200 tw-pt-3">
						<button
							class="tw-flex-1 tw-rounded-md tw-border tw-border-gray tw-bg-white tw-p-3 tw-font-semibold tw-text-gray"
							@click="onCustomCancel"
						>
							{{ $t('cancel') }}
						</button>
						<button
							:disabled="internalModelValue?.length != 2"
							class="tw-flex-1 tw-rounded-md tw-bg-brand tw-p-3 tw-font-semibold tw-text-white"
							@click="onCustomApply(selectDate)"
						>
							{{ $t('apply') }}
						</button>
					</div>
				</template>
			</VueDatePicker>
		</AppMenu>
	</div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { t } from '@/locales/index';
import AppMenu from '@/v2/components/app-menu';
import { periodOptions } from '@/v2/utils/dates';
import VueDatePicker from '@vuepic/vue-datepicker';

import '@vuepic/vue-datepicker/dist/main.css';
import { dateFormatter, loadFile, getAPIDateFormat } from '../../../../../utils';
import InlineSvg from '../../../../../components/inline-svg';

const props = defineProps({
	checkSubscription: {
		type: Boolean,
		default: false,
	},
});

const period = ref(periodOptions[1]);
const isMenuOpened = ref(false);
const customSelected = ref(false);
const menuRef = ref(null);
const date = ref();
const emit = defineEmits(['input']);

import { useAnalyticsStore } from '../../../stores/analytics';

const analyticsStore = useAnalyticsStore();

const options = computed(() => {
	return [
		...periodOptions,
		{
			id: 'custom',
			label: t('custom'),
		},
	];
});

const onChange = () => {
	let params = {};
	if (period.value.id === 'custom') {
		params = {
			date_from: getAPIDateFormat(date.value[0]),
			date_to: getAPIDateFormat(date.value[1]),
		};
	} else {
		params = {
			date_from: getAPIDateFormat(period.value.from()),
			date_to: getAPIDateFormat(period.value.to()),
		};
	}

	analyticsStore.fetchMainStatistics(params);
	analyticsStore.fetchConversationsStatistics(params);
	analyticsStore.fetchMessagesStatistics(params);
	analyticsStore.fetchAiEscalationsCount(params);

	// analyticsStore.fetchMessages(params);
};

const periodLabel = (label) => {
	if (label != t('custom')) return label;

	return date.value?.map((d) => dateFormatter(d)).join(' - ');
};

const onClose = () => {
	isMenuOpened.value = false;
	if (date.value && customSelected.value) {
		period.value = options.value[options.value.length - 1];
	}
	customSelected.value = false;
};
const onCustomCancel = () => {
	menuRef.value.close();
};
const onCustomApply = (applyFunc) => {
	console.log({ applyFunc });
	applyFunc();
	menuRef.value.close();
	onChange();
};

const selectOption = (opt) => {
	if (opt.id === 'custom') {
		isMenuOpened.value = true;
		customSelected.value = true;
		menuRef.value.open();
		return;
	}
	period.value = opt;
	date.value = undefined;
	onChange();
	menuRef.value.close();
};

onChange();
</script>

<style lang="scss" scoped>
::v-deep(.dp__menu) {
	border: 0;
	font-family: 'ar-font', Helvetica, 'sans-serif';
}
::v-deep(.dp__today) {
	border-radius: 50%;
	border: 1px solid #ec552f;
}
::v-deep(.dp__overlay_cell_active) {
	background: #ec552f;
}
::v-deep(.dp__range_start) {
	border-radius: 50%;
	background: #ec552f;
}
::v-deep(.dp__range_end) {
	border-radius: 50%;
	background: #ec552f;
}

button:disabled {
	opacity: 0.7;
}
</style>
